// Charteris K_t Phase 4: map the live payload's holdings[] into table rows with
// state badges, and the coverage object into stacked-bar segments. Pure — unit-tested.
// Generalised 2026-10-15 (was charterisKtHoldingsRows.ts): every live-synth fund (CHARTERIS, AMATI).
import type { LiveKtHolding, LiveKtCoverage } from '../api/client'

export type BadgeTone = 'live' | 'delayed' | 'stale' | 'proxy' | 'warn' | 'muted' | 'flat'
export interface KtBadge { key: string; label: string; tone: BadgeTone }
export interface KtHoldingRow {
  name: string
  symbol: string | null
  weightPct: number
  badge: KtBadge
  priceText: string
  returnPct: number
  contributionBp: number
  betaText: string
  /** The per-fund tag column: Charteris' silver share m, or Amati's roll future. */
  mText: string
}

const BADGES: Record<string, KtBadge> = {
  live: { key: 'live', label: 'Live', tone: 'live' },
  delayed: { key: 'delayed', label: 'Delayed', tone: 'delayed' },
  // A quote older than 45 min — the last close over a weekend or overnight. Rendered as '—' until 2026-10-10 while the
  // coverage bar counted it as delayed; now its own badge and segment.
  stale: { key: 'stale', label: 'Stale', tone: 'stale' },
  proxy: { key: 'proxy', label: 'Proxy', tone: 'proxy' },
  suspect: { key: 'suspect', label: 'Suspect', tone: 'warn' },
  uncovered: { key: 'uncovered', label: 'No price', tone: 'muted' },
}

export function badgeFor(state: string): KtBadge {
  return BADGES[state] ?? { key: 'unknown', label: '—', tone: 'muted' }
}

function priceText(price: number | null): string {
  if (price == null) return '—'
  return price < 1 ? price.toFixed(4) : price.toFixed(2)
}

/** Charteris' silver share m (2 dp), else Amati's roll future (GC / SI / HG), else '—' (carried, no roll). */
function tagText(h: LiveKtHolding): string {
  if (h.m != null) return h.m.toFixed(2)
  return h.driver ?? '—'
}

export function mapLiveKtHoldings(holdings: LiveKtHolding[]): KtHoldingRow[] {
  return [...holdings]
    .sort((a, b) => Math.abs(b.contribution_bp) - Math.abs(a.contribution_bp))
    .map((h) => ({
      name: h.name,
      symbol: h.symbol,
      weightPct: h.weight_pct,
      badge: badgeFor(h.state),
      priceText: priceText(h.price),
      returnPct: h.return_pct,
      contributionBp: h.contribution_bp,
      betaText: h.beta.toFixed(2),
      mText: tagText(h),
    }))
}

export interface CoverageSegment { key: string; label: string; pct: number; tone: BadgeTone }

export function coverageSegments(c: LiveKtCoverage): CoverageSegment[] {
  const segs: CoverageSegment[] = [
    { key: 'live', label: 'Live', pct: c.live_pct, tone: 'live' },
    { key: 'delayed', label: 'Delayed', pct: c.delayed_pct, tone: 'delayed' },
    { key: 'stale', label: 'Stale', pct: c.stale_pct ?? 0, tone: 'stale' },
    { key: 'proxy', label: 'Proxy', pct: c.proxy_pct, tone: 'proxy' },
    { key: 'uncovered', label: 'No price', pct: c.uncovered_pct, tone: 'muted' },
  ]
  // Amati (2026-10-15): cash and warrant marks held flat. Only when present, so Charteris keeps its five segments.
  if (c.flat_pct != null && c.flat_pct > 0) segs.push({ key: 'flat', label: 'Cash', pct: c.flat_pct, tone: 'flat' })
  return segs
}
